import React, { useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { ObjectItem } from 'api/clients/types';
import { useManageObjectModalV2Store } from 'components/ClientTable/Modals/ManageObjectModalV2/ManageObjectModalV2Store';

interface MarkerData {
  clientId: number;
  clientName: string;
  object_data: ObjectItem;
}

interface Props {
  markers: MarkerData[];
  appliedClientFilter: number[];
  activityFilter: boolean | null;
  handleZoomToMarker: (position: [number, number]) => void;
  handleSetOnMap: (obj: ObjectItem) => void;
}

const ClientMarkerList: React.FC<Props> = ({
  markers,
  appliedClientFilter,
  activityFilter,
  handleZoomToMarker,
  handleSetOnMap,
}) => {
  const [openClientIds, setOpenClientIds] = useState<number[]>([]);
  const toggleClient = (clientId: number) => {
    setOpenClientIds(prev => (prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]));
  };
  const { t } = useTranslation('translation', { keyPrefix: 'ClientSidebar' });
  const groupedClients = useMemo(() => {
    return Object.values(
      markers.reduce((acc: Record<number, { clientId: number; clientName: string; objects: ObjectItem[] }>, marker) => {
        const clientMatch = appliedClientFilter.length === 0 || appliedClientFilter.includes(marker.clientId);
        const activityMatch = activityFilter === null || marker.object_data.is_activity === activityFilter;

        if (clientMatch && activityMatch) {
          if (!acc[marker.clientId]) {
            acc[marker.clientId] = {
              clientId: marker.clientId,
              clientName: marker.clientName,
              objects: [],
            };
          }
          acc[marker.clientId].objects.push(marker.object_data);
        }
        return acc;
      }, {}),
    );
  }, [markers, appliedClientFilter, activityFilter]);

  const { onManageObjectModalV2Open } = useManageObjectModalV2Store();

  return (
    <div className="list-group">
      {groupedClients.map(clientGroup => {
        const isOpen = openClientIds.includes(clientGroup.clientId);
        return (
          <li key={clientGroup.clientName} className="list-group-item my-2 rounded-4 border">
            <strong className={`d-flex justify-content-between align-items-center ${isOpen ? 'border-bottom ' : ''}`}>
              <div className="fs-5 py-2  text-gray-700">{clientGroup.clientName}</div>
              <i
                onClick={() => toggleClient(clientGroup.clientId)}
                style={{ cursor: 'pointer' }}
                className="ri-arrow-down-s-fill fs-5"
              />
            </strong>
            {isOpen && (
              <div className="flex flex-column justify-content-start mt-2">
                {clientGroup.objects.map(obj => (
                  <React.Fragment key={obj.id}>
                    <li
                      className={`btn btn-ghost-dark text-black ${obj.is_activity && 'alert alert-danger'} my-1 w-100`}
                      onClick={() => (obj.position ? handleZoomToMarker(obj.position as [number, number]) : '')}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <div>{obj.name}</div>
                        {obj.position ? (
                          obj.is_activity && <i className="ri-alert-fill text-danger fs-24" />
                        ) : (
                          <Button className="rounded-4 btn-ghost-purple" onClick={() => handleSetOnMap(obj)}>
                            {t('setOnMap')}
                          </Button>
                        )}
                      </div>
                    </li>
                  </React.Fragment>
                ))}
                <div>
                  <Button
                    onClick={() => {
                      onManageObjectModalV2Open(undefined, undefined, clientGroup.clientId);
                    }}
                    className="btn text-center btn-ghost-success m-1"
                  >
                    {t('addSite')}
                  </Button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </div>
  );
};

export default ClientMarkerList;
